/**
 * Stop search, entirely in the browser.
 *
 * public/sites.json is a build-time snapshot of SL's network (see
 * scripts/fetch-sites.ts). It is fetched the first time you open setup and
 * then cached by the service worker, so searching costs no requests and works
 * with no signal.
 */

export type Site = {
  id: number;
  name: string;
  /** SL's own qualifier, present on a minority of stops. */
  note?: string;
  /**
   * "Stocksund, Danderyd". Only on stops whose name is not unique -- 931 of
   * them -- because those are the only ones that need telling apart. Written
   * by scripts/fetch-sites.ts; see the comment there for why SL cannot
   * supply it.
   */
  area?: string;
  lat?: number;
  lon?: number;
};

type Indexed = Site & { haystack: string };

let loading: Promise<Indexed[]> | null = null;

/**
 * Fold to something typeable on a hurried phone keyboard: case-insensitive,
 * and å/ä/ö matched by a/a/o. They are separate letters in Swedish and this
 * would be wrong for sorting -- but for search it means "malarhojden" finds
 * Mälarhöjden, which is the point.
 */
export const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export function loadSites(): Promise<Indexed[]> {
  loading ??= fetch(`${import.meta.env.BASE_URL}sites.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`sites.json: ${res.status}`);
      return res.json() as Promise<Site[]>;
    })
    .then((sites) =>
      sites.map((s) => ({
        ...s,
        // Area and note are searchable, not just displayed, so "ekbacken
        // danderyd" narrows ten identical rows to one in a single query.
        haystack: fold([s.name, s.note, s.area].filter(Boolean).join(' ')),
      })),
    )
    .catch((error: unknown) => {
      // Let the next attempt retry rather than caching the failure forever.
      loading = null;
      throw error;
    });
  return loading;
}

/**
 * Ranked matches. Roughly 6,500 stops is small enough that a linear scan per
 * keystroke is imperceptible, so there is no prefix tree here to get wrong.
 */
export function searchSites(sites: Indexed[], query: string, limit = 25): Site[] {
  const q = fold(query.trim());
  if (q.length < 2) return [];

  const hits: { site: Indexed; score: number }[] = [];

  for (const site of sites) {
    const at = site.haystack.indexOf(q);
    if (at === -1) continue;

    // Starts with what you typed, beats a match at a word boundary, beats a
    // match buried mid-word. "sol" should offer Solna before Farsta strand.
    //
    // Scored against the name alone. Ranking on the whole haystack would let
    // a stop match on its area -- every stop in Nacka scoring as though the
    // user had typed its name -- and bury the exact hit.
    const nameAt = fold(site.name).indexOf(q);
    const where =
      nameAt === -1 ? 3 : nameAt === 0 ? 0 : isBoundary(site, nameAt) ? 1 : 2;
    const score = where * 1000 + site.name.length;

    hits.push({ site, score });
  }

  hits.sort(
    (a, b) => a.score - b.score || a.site.name.localeCompare(b.site.name, 'sv'),
  );

  return hits.slice(0, limit).map(({ site: { haystack: _, ...site } }) => site);
}

const isBoundary = (site: Indexed, at: number) => fold(site.name)[at - 1] === ' ';

/**
 * Metres between two stops, on a sphere. Good to a fraction of a percent at
 * the distances this is used for, which is a few hundred metres.
 */
export function distance(a: Site, b: Site): number | null {
  if (a.lat === undefined || a.lon === undefined) return null;
  if (b.lat === undefined || b.lon === undefined) return null;
  const R = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Other stops close enough to walk to without thinking about it.
 *
 * SL splits places into more sites than a person would: Odenplan's bus
 * terminal and its metro station are two separate sites, and a road with
 * stops on both sides can be two more. So a line you are certain serves
 * "your" stop may in fact sit on a neighbouring one, and the picker offers
 * these as somewhere else to look.
 */
export function nearbySites(
  sites: Site[],
  origin: Site,
  { radius = 400, limit = 6 }: { radius?: number; limit?: number } = {},
): { site: Site; metres: number }[] {
  const out: { site: Site; metres: number }[] = [];
  for (const site of sites) {
    if (site.id === origin.id) continue;
    const metres = distance(origin, site);
    if (metres === null || metres > radius) continue;
    out.push({ site, metres });
  }
  return out.sort((a, b) => a.metres - b.metres).slice(0, limit);
}
